import {
  parseCurrencyType,
  type CurrencyType,
} from './hierarchy'

export type HierarchyStatementApiRow = {
  transferId: string
  book?: string
  kind?: string
  direction: 'in' | 'out'
  amount: string
  balanceBefore: string
  balanceAfter: string
  settledAfter?: string
  availableAfter?: string
  resolved?: boolean
  timestamp: string
  description?: string
  counterpartyId?: string | null
}

export type HierarchyStatementApi = {
  walletId: string
  openingBalance: string
  closingBalance: string
  unresolvedCount?: number
  rows: HierarchyStatementApiRow[]
}

export type ConsoleStatementRow = {
  id: string
  at: string
  description: string
  amount: number
  balanceBefore: number | null
  balanceAfter: number
  counterparty: string | null
  book: string
}

export type ConsoleStatement = {
  openingBalance: number
  closingBalance: number
  unresolvedCount: number
  rows: ConsoleStatementRow[]
}

/** Map hierarchy bank-statement (minor units) → display rows with prev/next. */
export function mapHierarchyStatement(
  live: HierarchyStatementApi,
  currencyRaw?: string | null,
): ConsoleStatement {
  const ccy: CurrencyType = parseCurrencyType(currencyRaw)
  const per = ccy === 'POINTS' ? 10_000 : 100
  const toDisplay = (minor: string | number | null | undefined) => {
    if (minor == null || String(minor).trim() === '') return 0
    const n = Number(minor)
    if (!Number.isFinite(n)) return 0
    return n / per
  }
  const rows: ConsoleStatementRow[] = (live.rows ?? []).map((row) => {
    const amountAbs = Math.abs(toDisplay(row.amount))
    const signed = row.direction === 'out' ? -amountAbs : amountAbs
    const afterMinor = row.balanceAfter ?? row.availableAfter ?? row.settledAfter
    const balanceAfter = toDisplay(afterMinor)
    const balanceBefore =
      row.balanceBefore != null && String(row.balanceBefore).trim() !== ''
        ? toDisplay(row.balanceBefore)
        : null
    return {
      id: row.transferId,
      at: row.timestamp,
      description: row.description?.trim() || (row.book === 'pnl' ? 'Settlement P&L' : 'Credit'),
      amount: signed,
      balanceBefore,
      balanceAfter,
      counterparty: row.counterpartyId ?? null,
      book: row.book || row.kind || '',
    }
  })
  return {
    openingBalance: toDisplay(live.openingBalance),
    closingBalance: toDisplay(live.closingBalance),
    unresolvedCount: live.unresolvedCount ?? 0,
    rows,
  }
}
